import type { Driver, Transfer, TransferKind } from './logistics-api';

/**
 * Logistics — the driver sheets (day and week): what goes on them, and the
 * WhatsApp text the Copy buttons produce.
 *
 * Pure and framework-free (no React, no next-intl import). The printed sheet
 * (components/logistics/driver-sheet.tsx) reads the SAME facts through
 * {@link rideFacts}, so the paper and the message never disagree.
 *
 * Redesigned 261015 (ArtDent: "this isn't understandable enough for the
 * drivers"). A driver reads it on a phone, often in a car, so every ride says
 * in words what to do and where — one labelled fact per line, the job first:
 *
 *   *DAY SHEET — Ermal*
 *   Mon 27 Jul 2026 · 2 rides
 *
 *   *1) 09:30 · PICK UP AT THE AIRPORT*
 *   👤 *Maria Rossi* · 2 people
 *   ✈️ Flight FR1234 · lands 11:00
 *   📍 From: TIA Airport
 *   🏁 To: Hotel Colosseo
 *   📞 +39 333 123 4567
 *   📝 Note: wheelchair assistance
 *
 * `*…*` is WhatsApp bold. A missing pickup time or destination is SAID
 * ("time to be confirmed", "not set — ask the office") rather than left out:
 * a blank is exactly what a driver cannot act on. Other optional lines (no
 * flight, one passenger, no note) are left out.
 *
 * Cancelled rides are excluded (a driver must not be handed a dead ride).
 * Rides are time-ordered with unscheduled ones FIRST, mirroring the board.
 */

/**
 * Optional translator. Deliberately narrow (`key`, optional flat values) so
 * next-intl's `useTranslations('logistics')` result drops straight in, while
 * non-React callers can pass nothing and get the English fallbacks below.
 */
export type DaySheetTranslate = (
  key: string,
  values?: Record<string, string | number>,
) => string;

/** Only the driver's display name is needed — Driver and the embedded ref both fit. */
export type DaySheetDriver = Pick<Driver, 'name'>;

/** English fallbacks, used verbatim when no translator is supplied. */
const FALLBACK: Record<string, string> = {
  'sheet.dayTitle': 'DAY SHEET',
  'sheet.weekTitle': 'WEEK SHEET',
  'sheet.job.arrival_pickup': 'Pick up at the airport',
  'sheet.job.departure_dropoff': 'Take to the airport',
  'sheet.job.clinic_ride': 'Clinic ride',
  'sheet.job.custom': 'Ride',
  'sheet.noTime': 'Time to be confirmed',
  'sheet.flight': 'Flight',
  'sheet.lands': 'lands',
  'sheet.takesOff': 'takes off',
  'sheet.from': 'From',
  'sheet.to': 'To',
  'sheet.askOffice': 'not set — ask the office',
  'sheet.note': 'Note',
  'sheet.noRides': 'No rides',
  'sheet.noAirportRides': 'No airport rides',
};

/** Short weekday / month names — matching the "Mon 27 Jul 2026" header shape. */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * `YYYY-MM-DD` → "Mon 27 Jul 2026".
 *
 * Parsed component-wise (NOT `new Date('YYYY-MM-DD')`, which is UTC-anchored and
 * can render the previous day west of Greenwich). An unparseable value is echoed
 * as-is rather than becoming "Invalid Date".
 */
export function formatDaySheetDate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const [, y, m, d] = match;
  const local = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(local.getTime())) return date;
  return `${WEEKDAYS[local.getDay()]} ${Number(d)} ${MONTHS[local.getMonth()]} ${y}`;
}

/**
 * `YYYY-MM-DD` → "Tuesday 20 Oct": a day heading on the sheets, where the
 * weekday is what a driver scans for. Unparseable input is echoed.
 */
export function formatSheetDayHeading(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const [, y, m, d] = match;
  const local = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(local.getTime())) return date;
  return `${WEEKDAYS_LONG[local.getDay()]} ${Number(d)} ${MONTHS[local.getMonth()]}`;
}

/** 'HH:mm:ss' | 'HH:mm' → 'HH:mm'. Anything else passes through untouched. */
export function normalizePickupTime(time: string | null): string | null {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return time;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

/**
 * Board/sheet ride ordering: unscheduled rides first (they need attention), then
 * ascending by time. Stable for equal times — the server's order wins there.
 */
export function compareTransfersByTime(a: Transfer, b: Transfer): number {
  const at = normalizePickupTime(a.pickup_time);
  const bt = normalizePickupTime(b.pickup_time);
  if (at === null && bt === null) return 0;
  if (at === null) return -1;
  if (bt === null) return 1;
  return at.localeCompare(bt);
}

/**
 * The ride kinds the WEEK sheet carries (ArtDent 261015): the airport legs
 * only — arrivals and departures. Clinic rides are planned day by day and stay
 * on the day sheet ("week sheet only contains rides from the airport … not the
 * clinic rides for now").
 */
export const WEEK_SHEET_KINDS: readonly TransferKind[] = ['arrival_pickup', 'departure_dropoff'];

/** Does this ride belong on the week sheet? See {@link WEEK_SHEET_KINDS}. */
export function isWeekSheetRide(transfer: Pick<Transfer, 'kind'>): boolean {
  return WEEK_SHEET_KINDS.includes(transfer.kind);
}

/** Marks a day's start in the week text — a heading must stand out on a phone. */
const DAY_RULE = '━━━━━━━━━━━━━━';

/** The facts a sheet prints for one ride — shared by the text and the printed page. */
export interface RideFacts {
  /** 'HH:mm', or null when the ride still needs a time. */
  time: string | null;
  /** What to do, in words: "Pick up at the airport". */
  job: string;
  /** "Flight FR1234 · lands 11:54", or null when neither is known. */
  flight: string | null;
  /** Where the car collects. Null = not set (the sheet says "ask the office"). */
  from: string | null;
  /** Where the car drops. Null = not set. */
  to: string | null;
  patient: string | null;
  phone: string | null;
  /** Head-count, only when more than one — "1 person" is noise. */
  people: number | null;
  note: string | null;
}

/** Read one ride as the sheets print it. */
export function rideFacts(ride: Transfer, t?: DaySheetTranslate): RideFacts {
  const tr = translator(t);
  const flightTime = normalizePickupTime(ride.flight_time ?? null);
  const flightParts: string[] = [];
  if (ride.flight_number) flightParts.push(`${tr('sheet.flight')} ${ride.flight_number}`);
  if (flightTime) {
    const verb = ride.kind === 'departure_dropoff' ? tr('sheet.takesOff') : tr('sheet.lands');
    flightParts.push(`${verb} ${flightTime}`);
  }
  const clean = (value: string | null | undefined): string | null =>
    value && value.trim() ? value.trim() : null;

  const flight = flightParts.join(' · ');

  return {
    time: normalizePickupTime(ride.pickup_time),
    job: tr(`sheet.job.${ride.kind}`),
    // "Lands 21:40" when there is no flight number to lead the line.
    flight: flight ? flight.charAt(0).toUpperCase() + flight.slice(1) : null,
    from: clean(ride.pickup_location),
    to: clean(ride.dropoff_location),
    patient: clean(ride.lead?.full_name),
    phone: clean(ride.lead?.phone),
    people: ride.passenger_count > 1 ? ride.passenger_count : null,
    note: clean(ride.notes),
  };
}

/** One day of a sheet: the day, and the rides on it as they print. */
export interface SheetDay {
  date: string;
  rides: Transfer[];
}

/**
 * The days a sheet prints: cancelled rides dropped, the board's order, and —
 * for the week sheet — only the airport legs ({@link isWeekSheetRide}).
 */
export function sheetDays(
  days: { date: string; transfers: Transfer[] }[],
  airportOnly: boolean,
): SheetDay[] {
  return days.map((day) => ({
    date: day.date,
    rides: liveRidesInOrder(airportOnly ? day.transfers.filter(isWeekSheetRide) : day.transfers),
  }));
}

/**
 * One driver's DAY as WhatsApp text.
 *
 * @param driver     the lane's driver (only `name` is read)
 * @param date       the board day, `YYYY-MM-DD`
 * @param transfers  that driver's rides for the day (cancelled ones are dropped)
 * @param t          optional translator scoped to the `logistics` namespace
 */
export function buildDaySheetText(
  driver: DaySheetDriver,
  date: string,
  transfers: Transfer[],
  t?: DaySheetTranslate,
): string {
  const tr = translator(t);
  const [day] = sheetDays([{ date, transfers }], false);

  const lines: string[] = [
    `*${tr('sheet.dayTitle')} — ${driver.name}*`,
    `${formatDaySheetDate(date)} · ${tr('sheet.rides', { count: day.rides.length })}`,
  ];

  if (day.rides.length === 0) {
    lines.push('', tr('sheet.noRides'));
    return lines.join('\n');
  }

  for (const [index, ride] of day.rides.entries()) {
    lines.push('', ...rideLines(index + 1, ride, t));
  }

  return lines.join('\n');
}

/**
 * One driver's WEEK as WhatsApp text (ArtDent 261015): Monday to Sunday, the
 * airport legs only, each day under its own heading with its rides numbered
 * from 1, and the empty days kept in place as one quiet line so the driver
 * sees the whole week in order.
 *
 * @param driver  the driver (only `name` is read)
 * @param days    the week's days in order, each with that driver's rides
 * @param t       optional translator scoped to the `logistics` namespace
 */
export function buildWeekSheetText(
  driver: DaySheetDriver,
  days: { date: string; transfers: Transfer[] }[],
  t?: DaySheetTranslate,
): string {
  const tr = translator(t);
  const week = sheetDays(days, true);
  const first = week[0]?.date ?? '';
  const last = week[week.length - 1]?.date ?? first;
  const total = week.reduce((sum, day) => sum + day.rides.length, 0);

  const lines: string[] = [
    `*${tr('sheet.weekTitle')} — ${driver.name}*`,
    `${formatDaySheetDate(first)} → ${formatDaySheetDate(last)} · ${tr('sheet.airportRides', { count: total })}`,
  ];

  for (const day of week) {
    if (day.rides.length === 0) {
      lines.push('', `_${formatSheetDayHeading(day.date)} — ${tr('sheet.noAirportRides')}_`);
      continue;
    }
    lines.push('', DAY_RULE, `*${formatSheetDayHeading(day.date).toUpperCase()}*`, DAY_RULE);
    for (const [index, ride] of day.rides.entries()) {
      if (index > 0) lines.push('');
      lines.push(...rideLines(index + 1, ride, t));
    }
  }

  return lines.join('\n');
}

/**
 * The Monday-to-Sunday week holding `date`, as `YYYY-MM-DD` strings. Built
 * component-wise for the same reason formatDaySheetDate is.
 */
export function weekDatesOf(date: string): string[] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return [date];
  const [, y, m, d] = match;
  const local = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(local.getTime())) return [date];
  const sinceMonday = (local.getDay() + 6) % 7;
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(local);
    day.setDate(local.getDate() - sinceMonday + i);
    const mm = String(day.getMonth() + 1).padStart(2, '0');
    const dd = String(day.getDate()).padStart(2, '0');
    return `${day.getFullYear()}-${mm}-${dd}`;
  });
}

/** The translator, with the pluralised strings spelled out for the translator-less path. */
function translator(t?: DaySheetTranslate) {
  return (key: string, values?: Record<string, string | number>): string => {
    if (t) return t(key, values);
    const count = Number(values?.count ?? 0);
    if (key === 'sheet.rides') return `${count} ${count === 1 ? 'ride' : 'rides'}`;
    if (key === 'sheet.airportRides') return `${count} airport ${count === 1 ? 'ride' : 'rides'}`;
    if (key === 'sheet.people') return `${count} ${count === 1 ? 'person' : 'people'}`;
    return FALLBACK[key] ?? key;
  };
}

/** Cancelled rides dropped, unscheduled first, then by time — the board's order. */
export function liveRidesInOrder(transfers: Transfer[]): Transfer[] {
  return transfers
    .filter((transfer) => transfer.status !== 'cancelled')
    .slice()
    .sort(compareTransfersByTime);
}

/** One ride as WhatsApp lines: the job in bold, then one labelled fact per line. */
function rideLines(number: number, ride: Transfer, t?: DaySheetTranslate): string[] {
  const tr = translator(t);
  const facts = rideFacts(ride, t);
  const askOffice = tr('sheet.askOffice');

  const lines = [
    `*${number}) ${facts.time ?? tr('sheet.noTime').toUpperCase()} · ${facts.job.toUpperCase()}*`,
  ];
  // Who, straight under the job and in bold — the name the driver asks for.
  if (facts.patient) {
    const people = facts.people ? ` · ${tr('sheet.people', { count: facts.people })}` : '';
    lines.push(`👤 *${facts.patient}*${people}`);
  }
  if (facts.flight) lines.push(`✈️ ${facts.flight}`);
  lines.push(`📍 ${tr('sheet.from')}: ${facts.from ?? askOffice}`);
  lines.push(`🏁 ${tr('sheet.to')}: ${facts.to ?? askOffice}`);
  if (facts.phone) lines.push(`📞 ${facts.phone}`);
  if (facts.note) lines.push(`📝 ${tr('sheet.note')}: ${facts.note}`);

  return lines;
}
